import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';

import { appeler } from '../../api/client.ts';
import { Carte, Chargement, Erreur, Vide } from '../../composants/communs.tsx';
import { Montant, Badge } from '../../composants/ui/index.ts';

/**
 * Historique consolidé d'un client — RG-197.
 *
 * Achats, règlements et retours sur une seule frise. C'est l'écran qu'on
 * ouvre quand quelqu'un conteste ce qu'il doit : il fallait jusqu'ici en
 * ouvrir trois et recouper à la main.
 *
 * La frise est CONSOLIDÉE, toutes boutiques confondues, comme le solde
 * (`RG-096`). Le code de la boutique figure sur chaque ligne : rien n'est
 * masqué, tout est attribué.
 */

interface Evenement {
  type: 'VENTE' | 'REGLEMENT' | 'RETOUR';
  id: string;
  numero: string | null;
  boutique_code: string;
  horodatage: string;
  montant: number;
  reste_du: number | null;
  statut: string | null;
}

interface Frise {
  totaux: {
    achete: string;
    nombre_ventes: string;
    regle: string;
    rendu: string;
    derniere_vente: string | null;
  };
  evenements: Evenement[];
  curseur_suivant: string | null;
}

const LIBELLE: Record<Evenement['type'], string> = {
  VENTE: 'Achat',
  REGLEMENT: 'Règlement',
  RETOUR: 'Retour',
};

export function Historique({
  clientId,
  nom,
  onFerme,
}: {
  clientId: string;
  nom: string;
  onFerme: () => void;
}): ReactNode {
  /**
   * Les pages sont EMPILÉES, pas remplacées.
   *
   * « Voir plus » doit allonger la frise. La remplacer obligerait à revenir
   * en arrière pour relire ce qu'on vient de dépasser, sur un écran dont tout
   * l'intérêt est de suivre une chronologie.
   */
  const [curseurs, setCurseurs] = useState<string[]>([]);

  return (
    <Carte
      titre={`Historique — ${nom}`}
      actions={
        <button type="button" className="bouton--discret" onClick={onFerme}>
          Fermer
        </button>
      }
    >
      <Page
        clientId={clientId}
        curseur={null}
        premiere
        onSuite={(curseur) => {
          setCurseurs([curseur]);
        }}
      />

      {curseurs.map((curseur, rang) => (
        <Page
          key={curseur}
          clientId={clientId}
          curseur={curseur}
          premiere={false}
          onSuite={(suivant) => {
            setCurseurs([...curseurs.slice(0, rang + 1), suivant]);
          }}
        />
      ))}
    </Carte>
  );
}

/**
 * Une page de la frise.
 *
 * Chaque page est sa propre requête, donc son propre cache : revenir sur
 * l'écran ne redemande pas ce qui a déjà été chargé.
 */
function Page({
  clientId,
  curseur,
  premiere,
  onSuite,
}: {
  clientId: string;
  curseur: string | null;
  /** La première page porte les totaux ; les suivantes ne les répètent pas. */
  premiere: boolean;
  onSuite: (curseur: string) => void;
}): ReactNode {
  const { data, isPending, error } = useQuery({
    queryKey: ['historique', clientId, curseur],
    queryFn: () =>
      appeler<Frise>(
        `/clients/${clientId}/historique?limite=20` +
          (curseur === null ? '' : `&curseur=${encodeURIComponent(curseur)}`),
      ),
  });

  if (error !== null) {
    return <Erreur erreur={error} />;
  }
  if (isPending) {
    return <Chargement />;
  }
  if (premiere && data.evenements.length === 0) {
    return <Vide texte="Ce client n’a encore rien acheté." />;
  }

  return (
    <>
      {premiere && <Totaux totaux={data.totaux} />}

      <div className="tableau-defilant">
        <table>
          {premiere && (
            <thead>
              <tr>
                <th>Date</th>
                <th>Nature</th>
                <th>Pièce</th>
                <th>Boutique</th>
                <th className="num">Montant</th>
                <th className="num">Reste dû</th>
              </tr>
            </thead>
          )}
          <tbody>
            {data.evenements.map((evenement) => (
              <Ligne key={evenement.id} evenement={evenement} />
            ))}
          </tbody>
        </table>
      </div>

      {data.curseur_suivant !== null && (
        <button
          type="button"
          className="bouton--discret"
          style={{ marginTop: 12 }}
          onClick={() => {
            onSuite(data.curseur_suivant ?? '');
          }}
        >
          Voir plus
        </button>
      )}
    </>
  );
}

function Totaux({ totaux }: { totaux: Frise['totaux'] }): ReactNode {
  return (
    <div className="grille" style={{ marginBottom: 16 }}>
      <div>
        <div className="indicateur__libelle">Acheté</div>
        <div className="indicateur__valeur">
          <Montant valeur={Number(totaux.achete)} />
        </div>
        <div className="indicateur__note">
          {totaux.nombre_ventes} vente{Number(totaux.nombre_ventes) > 1 ? 's' : ''}
        </div>
      </div>
      <div>
        <div className="indicateur__libelle">Réglé</div>
        <div className="indicateur__valeur">
          <Montant valeur={Number(totaux.regle)} />
        </div>
      </div>
      <div>
        <div className="indicateur__libelle">Rendu</div>
        <div className="indicateur__valeur">
          <Montant valeur={Number(totaux.rendu)} />
        </div>
      </div>
    </div>
  );
}

function Ligne({ evenement }: { evenement: Evenement }): ReactNode {
  return (
    <tr>
      <td className="mono">{evenement.horodatage.slice(0, 10)}</td>
      <td>
        {evenement.type === 'VENTE' && evenement.statut === 'CONTREPASSEE' ? (
          <Badge statut="contrepassee" />
        ) : (
          LIBELLE[evenement.type]
        )}
      </td>
      <td className="mono">{evenement.numero ?? '—'}</td>
      <td className="mono">{evenement.boutique_code}</td>
      <td className="num">
        {/*
          Un règlement et un retour arrivent NÉGATIFS du serveur : la colonne
          s'additionne telle qu'elle s'affiche, sans que le lecteur ait à
          changer les signes de tête.
        */}
        <Montant valeur={evenement.montant} />
      </td>
      <td className="num">
        {evenement.reste_du === null || evenement.reste_du === 0 ? (
          <span className="muted">—</span>
        ) : (
          <span style={{ color: 'var(--brick)' }}>
            <Montant valeur={evenement.reste_du} />
          </span>
        )}
      </td>
    </tr>
  );
}
